// Angular
import { Injectable, Injector } from "@angular/core";
import { HttpClient, HttpEvent, HttpRequest } from "@angular/common/http";
// Environment 
import { environment } from "../../../environments/environment";
// Interfaces
import { IApplicationIDE } from "../interfaces/ApplicationIDE";
import { IInstallableApplicationIDE } from "../interfaces/InstallableApplicationIDE";
import { IMobileApp } from "../interfaces/MobileApp";
// Services
import { ReleaseService } from "./release.service";
import { SessionService } from "./session.service";
import { SessionSocketService } from "./session-socket.service";
import { ToasterService } from "./toaster.service";
import { UtilsService } from "./utils.services";
// Third Part
import { BehaviorSubject, Observable, Subject, Subscription } from "rxjs";
import { map, catchError, first } from "rxjs/operators";
import { Guid } from "guid-typescript";
import { IDeviceResponse } from "../interfaces/IDeviceResponse";
@Injectable({
  providedIn: "root",
})

/**
 * This Service keeps track of all the applications avaialble for the current ide session
 * To install an applcation simply provide the application guid or application package guid
 * to InititateApplcationInstallOrToggleSelected method which will update application objects as required
 * If an application install fails the service defaults to null application and the user has so select some other application
 * manually
 *
 * HomeComponent works mostly on ApplicationPackageGUIDs
 */
export class ApplicationsService {
  applicationTestData:any  = [];
  isInstallationFailed = false;
  appInstallStartTime: number = 0;

  envBase = environment.baseUrl;
  baseUrl = environment.baseUrl + "/api";
  parserURL = environment.parserURL;
  availableWebsites;

  lastTestRunID;
  currentWebsiteName;
  // application Id in lab

  applicationId$: BehaviorSubject<number> = new BehaviorSubject<number>(0);

  /**
   * List of all applications available from IDE
   */
  IdeApplications: BehaviorSubject<IInstallableApplicationIDE[]> =
    new BehaviorSubject<IInstallableApplicationIDE[]>([]);
  /**
   * Current Application Selected in IDE
   */
  SelectedApplication: BehaviorSubject<IInstallableApplicationIDE> =
    new BehaviorSubject<IInstallableApplicationIDE>(null);
  /**
   * This keeps track of avaialbility of List of applications
   */
  ApplicationsReady$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );
  /**
   * Disable All Actions while an application is being installed
   */
  DisableAllActions$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );
  /**
   * This is needed the app is already installed but needs selection within IDE
   */
  select_ApplicationPackageGUID$: Subject<string> = new Subject<string>();
  /**
   * This is needed when there are already manu apps installed for the session
   */
  AlreadyInstalledApplicationPackageNames: Subject<string[]> = new Subject<
    string[]
  >();

  parentGUID$: BehaviorSubject<string> = new BehaviorSubject<string>("");
  httpOptions = {};
  allSubscriptions: Subscription[] = [];
  constructor(
    private httpClient: HttpClient,
    private injector: Injector,
    private sessionService: SessionService,
    private utilsService: UtilsService,
    private toasterService: ToasterService
  ) {
    this.httpOptions = this.utilsService.httpOptions;
  }

  private setDownloadState(application: IInstallableApplicationIDE): void {
    application.downloadShow = true;
    application.loader = true;
  }

  initiateApplicationInstallOrToggleSelected(Guid: string): void {
    const sessionSocketService = this.injector.get(SessionSocketService);
    const apps = this.IdeApplications.getValue();
    let _application = null as IInstallableApplicationIDE,
      _otherApplications = null as IInstallableApplicationIDE[];
    //if build is changed from lab using the builds list as UserApplicationBuildList only has paginated data
    _application = apps.find((i) => 
      i.buildsList ? 
        i.buildsList.some(j => j.value === Guid) :
        i.UserApplicationBuildList.some(j => j.AppPackageGUID === Guid)
    );

    _otherApplications = apps.filter((i) => 
      i.buildsList ? 
        !i.buildsList.some(j => j.value === Guid) :
        !i.UserApplicationBuildList.some(j => j.AppPackageGUID === Guid)
    );
    _otherApplications.forEach((i) => {
      i.selected = false;
      i.install = false;
    });

    if (_application?.downloadShow === true && _application?.loader === true) {
      _application.downloadShow = false;
      _application.loader = false;
      _application.selected = true;
      _application.install = true;
      this.isInstallationFailed = false;
      this.DisableAllActions$.next(false);
    } else if (_application.install) {
      _application.selected = true;
    } else {
      this.setDownloadState(_application);
      this.DisableAllActions$.next(true);
      this.appInstallStartTime = Date.now();
      sessionSocketService.SendApplicationInstall(Guid);
    }

    this.SelectedApplication.next(_application);
  }
  /**
   * Set loading to true of application if its not installed else set it to selected application
   * @param Guid Should be Application Guid or Application Package Guid
   * @param flag set to false if providing Application Guid or true for Application Package GUID
   */
  initiateApplicationInstallOrToggleSelectedByApplicationId(
    _applicationId: number, buildId: string
  ): void {
    const apps = this.IdeApplications.getValue();
    const sessionSocketService = this.injector.get(SessionSocketService);
    let _application = null as IInstallableApplicationIDE,
      _otherApplications = null as IInstallableApplicationIDE[];
    _application = apps.filter((i) => i.ApplicationId == _applicationId)[0];
    _otherApplications = apps.filter((i) => i.ApplicationId != _applicationId);
    _otherApplications.forEach((i) => (i.selected = false));
    let index = 0;
    if(buildId){
      index = this.findSelectedBuildIndex(buildId, _application);
    } 
    if (_application.install) {
      apps.forEach((i) => (i.selected = false));
      _application.selected = true;
    } else {
      this.DisableAllActions$.next(true);
      this.appInstallStartTime = Date.now();
      sessionSocketService.SendApplicationInstall(
        _application.UserApplicationBuildList[index]?.AppPackageGUID
      );
      apps.forEach((i) => (i.selected = false));
      this.setDownloadState(_application);
    }
    this.SelectedApplication.next(_application);
  }

  findSelectedBuildIndex(buildId: string, application: IInstallableApplicationIDE): number {
    application.selectedBuildIndex = application.UserApplicationBuildList.findIndex((build: any) => build.AppPackageId === buildId) || 0;
    return application.selectedBuildIndex;
  }

  loadSelectedApplication(SessionGUID: string, DefaultApplicationGUID: string, buildId?: string) {
    this.ApplicationsReady$.next(false);

    this.allSubscriptions.push(
      this.fetchPerticularApplication(DefaultApplicationGUID).subscribe(
        (data) => {
          if (buildId) {
            this.findSelectedBuildIndex(buildId, data);
          }
          this.IdeApplications.next([data]);
          this.applicationId$.next(Number(data.ApplicationId));
        },
        (err) => {
          console.log(err);
          this.toasterService.ShowToastMessage(err.error.userMessage, 0);
        },
        () => {
          this.ApplicationsReady$.next(true);
        }
      ),
      this.AlreadyInstalledApplicationPackageNames.subscribe((list) =>
        this.setApplicationsInstalled(list)
    ));
  }

  fetchPerticularApplication(
    applicationGUID: string
  ): Observable<IInstallableApplicationIDE> {
    return this.httpClient
      .get(
        `${this.envBase}/application-microservice/application/${applicationGUID}?isIDE=true`,
        this.httpOptions
      )
      .pipe(
        map((data: { data: IApplicationIDE; message: string }) => {
          let res = data.data;
          return !res ? null : this.utilsService.appTransformation(res);
        })
      );
  }

  /**
   * Fetch Data from API and map for personal use
   * @param UserGUID
   */
  fetchListOfApplications(
    UserGUID: string,
    PlatformId: number = 1,
    filter: string
  ): Observable<IInstallableApplicationIDE[]> {
    let url = `${this.envBase
      }/application-microservice/applications/${this.parentGUID$.getValue()}?platformID=${PlatformId}`;
    if (filter) {
      url += `&filter=${filter}`;
    }

    return this.httpClient.get(url, this.httpOptions).pipe(
      map((data: { data: IApplicationIDE[]; message: string }) => {
        return data.data
          .filter((x) => x.UserApplicationBuildList.length)
          .map((app) => {
            return this.utilsService.appTransformation(app);
          });
      })
    );
  }
  /**
   * Set Application installed to true for list of applications by package name
   * @param ApplicationPackageNames String array containing Names of all applications installed
   */
  setApplicationsInstalled(ApplicationPackageNames: string[]) {
    const apps = this.IdeApplications.getValue();
    apps.forEach((i) => {
      if (ApplicationPackageNames.includes(i.PackageName)) {
        i.install = true;
      }
    });
  }
  /**
   * Defaults the selected application to null and resets its UI parameters
   * @param Guid Should be Application Guid or Application Package Guid
   * @param flag set to false if providing Application Guid or true for Application Package GUID
   */
  applicationInstallFailed(Guid: string, flag = false): void {
    const apps = this.IdeApplications.getValue();
    let _application = null,
      _otherApplications = null;
    if (flag) {
      _application = apps.filter(
        (i) => i.UserApplicationBuildList[0].AppPackageGUID === Guid
      )[0];
      _otherApplications = apps.filter(
        (i) => i.UserApplicationBuildList[0].AppPackageGUID !== Guid
      );
    } else {
      _application = apps.filter((i) => i.ApplicationGUID === Guid)[0];
      _otherApplications = apps.filter((i) => i.ApplicationGUID !== Guid);
    }
    this.isInstallationFailed = true;
    _application.install = false;
    _application.loader = false;
    _application.downloadShow = false;
    _application.selected = false;
    _otherApplications.forEach((i) => (i.selected = false));
    // select first App
    this.SelectedApplication.next(apps[0]);
    this.DisableAllActions$.next(false);
  }

  /**
   * Parse Upload Mobile App Upload Data from API Response
   * @param body Response body of upload api
   */
  parseUploadFileDetailsFromResponse(body: any): IMobileApp {
    return {
      packageName: body.packageName || "",
      appFileLocation: body.fileLocation || "",
      packageMD5: body.hash || "",
      platform: body.platform || "",
      deviceTemplateId: 1,
      deviceOSParameter: "",
      deviceTypeList: "",
      originalFileName: body.originalFileName || "",
      appIcon: body.appIcon || "",
      size: body.fileSize || "",
      currentVersion: body.version || "",
      minimum_sdk: `API Level ${body.minSdk || ''}`,
      minSdk: body.minSDK || null,
      targetSdk: body.targetSDK || null,
      version: body.version || "",
      applicationGuid: body.applicationGuid || "",
      appPackageGuid: body.appPackageGuid || "",
      platformId: body.platformId || 0,
      isNewApp: body.isNewApp
    } as IMobileApp;
  }

  /**
   * Add New Application from Parsed APK Data
   * @param userGUID user guid
   * @param _mobileAppMetaData mobile app meta data
   */
  uploadNewApplication(userGUID, _mobileAppMetaData): Observable<any> {
    const toasterService = this.injector.get(ToasterService);
    const data = {
      packageName: _mobileAppMetaData.packageName,
      fileLocation: _mobileAppMetaData.fileLocation,
      platform: _mobileAppMetaData.platform,
      originalFileName: _mobileAppMetaData.originalFileName,
      appIcon: _mobileAppMetaData.appIcon,
      minSDK: _mobileAppMetaData.minSdk,
      targetSDK: _mobileAppMetaData.targetSdk,
      version: _mobileAppMetaData.version,
      liveUserID: _mobileAppMetaData.liveUserID,
      userID: _mobileAppMetaData.userID,
      hashValue: _mobileAppMetaData.hashValue,
      applicationGuid: _mobileAppMetaData.applicationGuid,
      appPackageGuid: _mobileAppMetaData.appPackageGuid,
      platformId: _mobileAppMetaData.platformId,
      isNewApp: _mobileAppMetaData.isNewApp,
      sizeInBytes: _mobileAppMetaData.sizeInBytes
    };
    return this.httpClient
      .post(
        this.envBase + `/application-microservice/applications/${userGUID}`,
        data,
        this.httpOptions
      )
      .pipe(
        first(),
        map((list: any) => {
          const applicationDetail = list.data;
          return applicationDetail;
        }),
        catchError((err) => {
          toasterService.ShowToastMessage("Application Upload Failed", 0, 3000);
          return new Observable((o) => o.error(err));
        })
      );
  }

  updateDeviceAcquiredHubspot(sessionGUIDRecieved: string) {
    return this.httpClient.post<any[]>(
      this.baseUrl + "/BotManagement/UpdateDeviceAcquired",
      {
        Flag: true,
        SessionGUID: sessionGUIDRecieved,
      },
      this.httpOptions
    );
  }

  updateIDESessionApplicationTrack() {
    const rls = this.injector.get(ReleaseService);
    if(rls.CurrentRelease.getValue()?.ReleaseModelId === null){
      return
    }
    const packageGuid = this.SelectedApplication.getValue().buildsList ? this.SelectedApplication.getValue().buildsList[
      this.SelectedApplication.getValue().selectedBuildIndex
    ].value : this.SelectedApplication.getValue().UserApplicationBuildList[
      this.SelectedApplication.getValue().selectedBuildIndex
    ].AppPackageGUID;
    this.httpClient
      .put(
        environment.baseUrl +
        `/session-microservice/session/${this.sessionService.SessionGUID
        }/createAppTrack/${packageGuid}`,
        {
          releaseId: rls.CurrentRelease.getValue()?.ReleaseModelId ?? "",
        },
        this.httpOptions
      )
      .subscribe();
  }
  UploadFile(appFile: File, UserGUID: string): Observable<HttpEvent<any>> {
    const url = `${this.parserURL}/${UserGUID}`;
    const formData = new FormData();
    formData.append("appFile", appFile);

    const req = new HttpRequest("POST", url, formData, {
      reportProgress: true,
    });

    return this.httpClient.request(req);
  }

  SetApplicationDownloadPercentage(percent: number) {
    const _apps = this.IdeApplications.getValue();
    if (_apps) {
      const app = _apps.filter(
        (i) => i.loader === true && i.downloadShow === true
      )[0];
      if (app) {
        app.downloadPercentage = percent;
      }
    }
  }

  getListOfTestDataByApplicationID(applicationId: string) {
    let url =
      this.envBase + `/mobile-microservice/test-data/${applicationId}/app`;

    return this.httpClient.get<any>(url, this.httpOptions).pipe(
      map((data) => {
        data = data.data;
        this.applicationTestData = data;
     
        return data;
      })
    );
  }

  getTestDataName(item) {
    if (
      ![
        "ADD_TEST_DATA",
        "SAVE_TEST_CASE_VARIABLE",
        "SET_TEST_CASE_VARIABLE",
        "SET_ARITHMETIC_VARIABLE",
        "DEEP_LINK_WITH_VARIABLE",
      ].includes(item.actionType)
    ) {
      return `${item?.actionValue || "N/A"}`;
    } else {
      let testData = this.applicationTestData.find(
        (x) => x.TestDataID == item.actionValue
      );
      return testData ? `${testData.VariableName.trim()}` : "--";
    }
  }

  getTestData(item) {
    if (
      [
        "ADD_TEST_DATA",
        "SAVE_TEST_CASE_VARIABLE",
        "SET_TEST_CASE_VARIABLE",
        "SET_ARITHMETIC_VARIABLE"
      ].includes(item.action)
    ) {
      return item.actionSubValue || "N/A";
    } else if (!["ADD_TEST_DATA"].includes(item.action)) {
      if (item.action == "CLICK") {
        return item.actionValue == "--"
          ? item.selectedComponent.text != ""
            ? item.selectedComponent.text
            : "--"
          : item.actionValue;
      } else {
        return item.actionValue || "N/A";
      }
    } 
    
  }

  getSubscriptionsDetails(userGUID: string): Observable<any> {
    return this.httpClient.get<any>(
      this.envBase + "/subscription-microservice/user/subscription/" + userGUID,
      this.httpOptions
    );
  }

  getXML(URL: string) {
    return this.httpClient.get(URL, { responseType: 'text' }).toPromise();
  }

  getBuildDetails(appPackageId: string) {
    const URL = `/application-microservice/application-package/build-details/${appPackageId}`;
    return this.httpClient.get<any>(this.envBase + URL, this.httpOptions);
  }

  getDeviceDetail(deviceId: string): Observable<IDeviceResponse> {
    const query = `/device-microservice/device/${deviceId}`;
    return this.httpClient.get<any>(this.envBase + query, this.httpOptions);;
  }

  getListOfPreviousUploadsOfApplication(
    applicationGuid: string,
    pageNo?: number,
    filter?: string
  ): Observable<any>
  {
    let URL = `/application-microservice/application-package/${applicationGuid}/previous-uploads`;
    if (pageNo) {
      URL += `?pageNo=${pageNo}`;
    }
    if (filter) {
      URL += `&filter=${filter}`;
    }
    return this.httpClient.get(environment.baseUrl + `${URL}`);
  }

  ngOnDestroy() {
    this.allSubscriptions.forEach((item) => item.unsubscribe());
  }
}
